import React, { useEffect, useState } from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  LayoutDashboard, Building2, BedDouble, CalendarDays, Users,
  Wrench, Star, BarChart3, LineChart, Settings, FileEdit,
  Home, LogOut, ChevronLeft, ChevronRight, Clock, AlertTriangle,
  ClipboardList, Lightbulb, LifeBuoy
} from 'lucide-react';
import HotelStatus from '../components/HotelStatus';
import HotelStatusPage from './HotelStatusPage';
import HotelRegistration from './HotelRegistration';
import OwnerSettingsPage from './OwnerSettingsPage';
import MyProperties from './MyProperties';
import './HotelOwnerDashboard.css';

/**
 * HotelOwnerDashboard
 * Main dashboard layout with sidebar navigation
 * Shows different navigation items based on hotel registration status
 */
const HotelOwnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, hotelStatus, hotelStatusLoading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    // Verify user has owner role
    if (userProfile && userProfile.role !== 'owner') {
      toast.error('Access denied. Owner privileges required.');
      navigate('/dashboard');
      return;
    }
  }, [userProfile, navigate]);

  // Handle redirects based on hotel status
  useEffect(() => {
    if (hotelStatusLoading) return; // Wait for status to load
    
    const status = hotelStatus?.status;
    const currentPath = location.pathname;
    
    console.log('🔀 Routing check:', { status, currentPath, hotelStatus });
    
    // Redirect logic based on status and current path
    // For all statuses, allow access to /owner (dashboard)
    // For pending/declined/approved/not_registered: stay on /owner (will show dashboard with sidebar)
    
    // Block access to restricted pages for non-approved users
    if (status !== 'approved') {
      const allowedPaths = ['/owner', '/owner/status', '/owner/register', '/owner/settings', '/owner/properties'];
      const isAllowed = allowedPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));
      
      if (!isAllowed && currentPath.startsWith('/owner/')) {
        console.log('→ Blocking restricted page access, redirecting to dashboard');
        navigate('/owner', { replace: true });
      }
    }
  }, [hotelStatus, hotelStatusLoading, location.pathname, navigate]);

  const handleStatusChange = (status) => {
    // Status is now managed in AuthContext, so this function can be simplified
    // or removed if no longer needed. For now, keeping it for compatibility
    console.log('Status changed:', status);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  // Navigation items based on status
  const getNavigationItems = () => {
    // Treat null hotelStatus as 'not_registered' for navigation
    const status = hotelStatus?.status ?? 'not_registered';

    // For pending/declined/not_registered users: only show status, register, and settings
    if (status === 'pending' || status === 'declined' || status === 'not_registered' || status === 'null') {
      return [
        { path: 'status', Icon: Home, label: 'Status', exact: true },
        { path: 'register', Icon: FileEdit, label: status === 'not_registered' || status === 'null' ? 'Register Property' : 'Edit Registration' },
        { path: 'settings', Icon: Settings, label: 'Account' },
      ];
    }

    // For approved users: show full dashboard
    return [
      { path: '', Icon: LayoutDashboard, label: 'Dashboard', exact: true },
      { path: 'properties', Icon: Building2, label: 'Properties' },
      { path: 'rooms', Icon: BedDouble, label: 'Rooms' },
      { path: 'bookings', Icon: CalendarDays, label: 'Bookings' },
      { path: 'staff', Icon: Users, label: 'Staff' },
      { path: 'maintenance', Icon: Wrench, label: 'Maintenance' },
      { path: 'reviews', Icon: Star, label: 'Reviews' },
      { path: 'reports', Icon: BarChart3, label: 'Reports' },
      { path: 'analytics', Icon: LineChart, label: 'Analytics' },
      { path: 'settings', Icon: Settings, label: 'Settings' },
    ];
  };

  const isActivePath = (path, exact = false) => {
    const currentSubPath = location.pathname.replace('/owner', '').replace(/^\/$/, '') || '';
    if (exact) {
      return currentSubPath === path || (path === '' && currentSubPath === '');
    }
    return currentSubPath.startsWith('/' + path) || (path === '' && currentSubPath === '');
  };

  // Debug logging
  console.log('🔍 HotelOwnerDashboard render:', { 
    hotelStatusLoading, 
    hotelStatus, 
    userProfile: userProfile?.email 
  });

  // Show loading state only while actively fetching
  if (hotelStatusLoading && hotelStatus === null) {
    return (
      <div className="owner-fullscreen-state">
        <div className="owner-fullscreen-card">
          <div className="owner-spinner" aria-hidden="true" />
          <h3>Preparing your portal</h3>
          <p>Just a moment — we're fetching your registration status.</p>
        </div>
      </div>
    );
  }

  // If loading is done but status is still null, treat as not_registered
  if (!hotelStatusLoading && hotelStatus === null) {
    return (
      <div className="owner-fullscreen-state">
        <div className="owner-fullscreen-card owner-fullscreen-card--cta">
          <div className="owner-fullscreen-icon" aria-hidden="true">
            <FileEdit size={28} strokeWidth={1.6} />
          </div>
          <h3>Welcome to InnBotics</h3>
          <p>You haven't registered a property yet. Begin your application to unlock the full owner suite.</p>
          <div className="owner-fullscreen-actions">
            <button
              className="owner-btn owner-btn--primary"
              onClick={() => navigate('/owner/register')}
            >
              Begin Registration
            </button>
            <button
              className="owner-btn owner-btn--ghost"
              onClick={handleLogout}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navigationItems = getNavigationItems();
  const isPendingOrDeclined = hotelStatus?.status === 'pending' || hotelStatus?.status === 'declined';
  const isNotRegistered = hotelStatus?.status === 'not_registered';

  // Don't redirect - HotelOwnerRouteGuard already handles navigation logic
  // This component should just display the appropriate content based on status

  const ownerName = userProfile?.fullName || userProfile?.full_name || 'Owner';
  const firstName = ownerName.split(' ')[0];
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return 'Good evening';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div className="hotel-owner-dashboard-layout">
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Owner Suite</span>
              </span>
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar" aria-hidden="true">
            {ownerName.charAt(0).toUpperCase()}
          </div>
          {sidebarOpen && (
            <div className="user-info">
              <h3>{ownerName}</h3>
              <p>{userProfile?.email}</p>
              {hotelStatus && (
                <span className={`status-badge status-${hotelStatus.status}`}>
                  <span className="status-dot" aria-hidden="true" />
                  {hotelStatus.status === 'not_registered' ? 'Not registered' :
                   hotelStatus.status === 'pending' ? 'Pending review' :
                   hotelStatus.status === 'declined' ? 'Declined' :
                   hotelStatus.status === 'approved' ? 'Approved' : hotelStatus.status}
                </span>
              )}
            </div>
          )}
        </div>

        <nav className="sidebar-nav" aria-label="Owner navigation">
          {navigationItems.map((item) => {
            const Icon = item.Icon;
            return (
              <button
                key={item.path}
                className={`nav-item ${isActivePath(item.path, item.exact) ? 'active' : ''}`}
                onClick={() => navigate(`/owner/${item.path}`)}
                title={!sidebarOpen ? item.label : undefined}
              >
                <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout} title={!sidebarOpen ? 'Sign out' : undefined}>
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="dashboard-main-content">
        {/* Status banner for pending/declined */}
        {isPendingOrDeclined && (
          <div className={`status-banner status-${hotelStatus.status}`}>
            <div className="status-banner-content">
              <span className="status-icon" aria-hidden="true">
                {hotelStatus.status === 'pending'
                  ? <Clock size={22} strokeWidth={1.75} />
                  : <AlertTriangle size={22} strokeWidth={1.75} />}
              </span>
              <div className="status-text">
                <strong>
                  {hotelStatus.status === 'pending'
                    ? 'Your application is under review'
                    : 'Your application was declined'}
                </strong>
                <p>
                  {hotelStatus.status === 'pending'
                    ? 'Our hospitality team typically completes reviews within 2–3 business days.'
                    : 'Review the feedback below and resubmit when ready. We\'re happy to help.'}
                </p>
              </div>
              <button
                className="view-status-btn"
                onClick={() => navigate('/owner/status')}
              >
                View details
              </button>
            </div>
          </div>
        )}

        {/* Route content */}
        <div className="dashboard-content">
          <Routes>
            {/* Nested routes - all render within the layout */}
            <Route path="/" element={
              isPendingOrDeclined ? (
                <div className="dashboard-welcome">
                  <header className="welcome-header">
                    <p className="welcome-eyebrow">{greeting}</p>
                    <h1>{firstName},</h1>
                    <p className="welcome-lede">
                      Your owner suite is taking shape. Use the panels below to track your application,
                      refine your submission, or fine-tune your account.
                    </p>
                  </header>

                  <div className="welcome-cards">
                    <button type="button" className="welcome-card" onClick={() => navigate('/owner/status')}>
                      <span className="card-icon"><ClipboardList size={22} strokeWidth={1.6} /></span>
                      <h3>Application status</h3>
                      <p>Track review progress and timeline at a glance.</p>
                      <span className="card-cta">View status →</span>
                    </button>
                    <button type="button" className="welcome-card" onClick={() => navigate('/owner/register')}>
                      <span className="card-icon"><FileEdit size={22} strokeWidth={1.6} /></span>
                      <h3>{hotelStatus?.status === 'declined' ? 'Resubmit application' : 'Edit application'}</h3>
                      <p>Update property details, photos, and amenities.</p>
                      <span className="card-cta">{hotelStatus?.status === 'declined' ? 'Resubmit →' : 'Edit details →'}</span>
                    </button>
                    <button type="button" className="welcome-card" onClick={() => navigate('/owner/settings')}>
                      <span className="card-icon"><Settings size={22} strokeWidth={1.6} /></span>
                      <h3>Account</h3>
                      <p>Manage profile, contact, and notification preferences.</p>
                      <span className="card-cta">Open settings →</span>
                    </button>
                  </div>

                  <section className="info-section">
                    <header className="info-section-header">
                      <span className="section-eyebrow">While you wait</span>
                      <h2>Prepare your team for launch</h2>
                    </header>
                    <div className="info-grid">
                      <div className="info-box">
                        <span className="info-icon"><ClipboardList size={18} strokeWidth={1.7} /></span>
                        <h3>Onboarding playbook</h3>
                        <p>A guided tour of the owner suite — pricing, calendars, staff roles, and reporting.</p>
                      </div>
                      <div className="info-box">
                        <span className="info-icon"><Lightbulb size={18} strokeWidth={1.7} /></span>
                        <h3>Operational best practices</h3>
                        <p>Field-tested guidance on staffing, housekeeping cadence, and guest experience.</p>
                      </div>
                      <div className="info-box">
                        <span className="info-icon"><LifeBuoy size={18} strokeWidth={1.7} /></span>
                        <h3>Concierge support</h3>
                        <p>A dedicated specialist is available throughout your application — reach out anytime.</p>
                      </div>
                    </div>
                  </section>
                </div>
              ) : (
                <ApprovedOwnerDashboard ownerName={firstName} greeting={greeting} />
              )
            } />

            <Route path="status" element={<HotelStatusPage />} />
            <Route path="register" element={<HotelRegistration />} />
            <Route path="settings" element={<OwnerSettingsPage />} />
            <Route path="properties" element={<MyProperties />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

// Placeholder component for approved owner dashboard (will be fully implemented later)
const ApprovedOwnerDashboard = ({ ownerName = 'Owner', greeting = 'Welcome' }) => {
  const tiles = [
    { Icon: BarChart3, title: 'Performance', desc: 'Revenue, ADR, and occupancy at a glance — refreshed in real time.' },
    { Icon: Building2, title: 'Properties', desc: 'Curate listings, room types, and amenities across your portfolio.' },
    { Icon: CalendarDays, title: 'Reservations', desc: 'Today\'s arrivals, departures, and the pipeline ahead.' },
    { Icon: Star, title: 'Guest sentiment', desc: 'Review trends and reputation signals across major channels.' },
  ];
  return (
    <div className="approved-dashboard">
      <header className="dashboard-header">
        <p className="welcome-eyebrow">{greeting}</p>
        <h1>{ownerName},</h1>
        <p className="welcome-lede">
          Your full operations suite is on the way. Below is a preview of the modules you'll command from this page.
        </p>
      </header>

      <div className="placeholder-content">
        {tiles.map(({ Icon, title, desc }) => (
          <article key={title} className="placeholder-card">
            <span className="placeholder-icon"><Icon size={22} strokeWidth={1.6} /></span>
            <h3>{title}</h3>
            <p>{desc}</p>
          </article>
        ))}
      </div>
    </div>
  );
};

export default HotelOwnerDashboard;
